/**
 * Fridge.com Official Social Media Configuration
 *
 * IMPORTANT: Fridge.com only has the following social media accounts:
 * - Instagram: @fridgedotcom (www.instagram.com/fridgedotcom)
 * - Pinterest: @fridgedotcom (www.pinterest.com/fridgedotcom)
 * - YouTube: @fridgedotcom (www.youtube.com/@fridgedotcom)
 *
 * NO Facebook account
 * NO Twitter/X account
 *
 * Note: Share buttons for Facebook and Twitter are still provided
 * to allow users to share content on their preferred platforms.
 *
 * This configuration should be used throughout the site for:
 * - Social share buttons (all platforms for user convenience)
 * - Footer links (only platforms where we have accounts)
 * - SEO/Schema markup (only platforms where we have accounts)
 * - Any social media references
 *
 * URLs are the PLATFORM-CANONICAL www forms (FRIDGE-634, entity stacking):
 * each bare-domain form 301/308s to www — measured 08/29/2026 — and an
 * Organization sameAs link should assert identity directly, not through a
 * redirect hop.
 *
 * FRIDGE-634 entity-stacking inventory, every candidate probed 08/29/2026 —
 * sameAs carries ONLY profiles that exist AND are ours (Real-or-Hidden):
 * - TikTok/X @fridgedotcom RESOLVE but ownership is contested → FRIDGE-458
 *   (Owner Blocked); never add until settled OURS.
 * - Wikidata: no entity (wbsearchentities empty) → FRIDGE-299.
 * - Grokipedia: no Fridge.com article (checked via its own search).
 * - LinkedIn company, EIN newsroom profile, GitHub org: 404, none exist.
 * - Crunchbase: probe bot-blocked (403), no claimed profile on record.
 * The full gap list with blocking cards lives on FRIDGE-634.
 */

export const SOCIAL_MEDIA_ACCOUNTS = {
  instagram: {
    handle: '@fridgedotcom',
    url: 'https://www.instagram.com/fridgedotcom',
    name: 'Instagram',
    active: true
  },
  pinterest: {
    handle: '@fridgedotcom', 
    url: 'https://www.pinterest.com/fridgedotcom/',
    name: 'Pinterest',
    active: true
  },
  youtube: {
    handle: '@fridgedotcom',
    url: 'https://www.youtube.com/@fridgedotcom',
    name: 'YouTube',
    active: true
  },
  // Explicitly marking as inactive - DO NOT USE
  facebook: {
    handle: null,
    url: null,
    name: 'Facebook',
    active: false
  },
  twitter: {
    handle: null,
    url: null,
    name: 'Twitter',
    active: false
  }
} as const

/**
 * Get only active social media accounts
 */
export const getActiveSocialAccounts = () => {
  return Object.entries(SOCIAL_MEDIA_ACCOUNTS)
    .filter(([_, account]) => account.active)
    .reduce(
      (acc, [key, account]) => ({
        ...acc,
        [key]: account,
      }),
      {} as Partial<typeof SOCIAL_MEDIA_ACCOUNTS>
    )
}

/**
 * Social media profiles for SEO/Schema markup
 */
export const SOCIAL_MEDIA_PROFILES = [
  SOCIAL_MEDIA_ACCOUNTS.instagram.url,
  SOCIAL_MEDIA_ACCOUNTS.pinterest.url,
  SOCIAL_MEDIA_ACCOUNTS.youtube.url
].filter(Boolean)
